import { GroupingModeEnum, TotalModeEnum } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import type {
  CostUsageInfo,
  MaterialUsageInfo,
  ServiceUsage,
  ServiceUsagesGroup,
} from '../hooks/useFullyConsolidatedServiceUsages.hook';

import { formatQuotationGroupLabel } from './format-quotation-group-label.util';

export interface DevisPrestationsUsageAmountsRenderModel {
  readonly priceExcludingVat: Readonly<{ value: number; currencyId: Id }>;
  readonly discountPercentage: number | null;
  readonly discountAmount: Readonly<{ value: number; currencyId: Id }> | null;
  readonly vatId: Id;
}

export interface DevisPrestationsMaterialRenderModel {
  readonly id: Id;
  readonly code: string;
  readonly name: string;
  readonly description: string | null;
  readonly quantity: Readonly<{ value: number; unitId: Id }>;
}

export interface DevisPrestationsCostAmountRenderModel {
  readonly value: number;
  readonly currencyId: Id | null;
}

export interface DevisPrestationsCostMarginRenderModel {
  readonly label: string;
  readonly percentage: number | null;
  readonly amountPerUnit: DevisPrestationsCostAmountRenderModel;
}

export interface DevisPrestationsCostRenderModel {
  readonly id: Id;
  readonly code: string;
  readonly name: string;
  readonly description: string | null;
  readonly unitAmount: DevisPrestationsCostAmountRenderModel;
  readonly quantity: Readonly<{ value: number; unitId: Id }>;
  readonly margin: DevisPrestationsCostMarginRenderModel;
  readonly totalExcludingVatBeforeMargin: DevisPrestationsCostAmountRenderModel;
  readonly totalExcludingVatWithMargin: DevisPrestationsCostAmountRenderModel;
}

export interface DevisPrestationsUsageRenderModel {
  readonly id: Id;
  readonly title: string;
  readonly content: string | null;
  readonly quantity: Readonly<{ value: number; unitId: Id }> | null;
  readonly amounts: DevisPrestationsUsageAmountsRenderModel | null;
  readonly materials: readonly DevisPrestationsMaterialRenderModel[] | null;
  readonly costs: readonly DevisPrestationsCostRenderModel[] | null;
  readonly costsSubtotalForOneUnit: DevisPrestationsCostAmountRenderModel | null;
  readonly costsTotal: DevisPrestationsCostAmountRenderModel | null;
}

export interface DevisPrestationsGroupRenderModel {
  readonly id: Id;
  // Set on the first group of each sub-mission section of a collaboration.
  readonly sectionTitle: string | null;
  readonly label: string | null;
  readonly subtotal: Readonly<{ value: number; currencyId: Id }> | null;
  readonly usages: readonly DevisPrestationsUsageRenderModel[];
}

export interface DevisPrestationsRenderModel {
  readonly groupingMode: GroupingModeEnum;
  readonly groups: readonly DevisPrestationsGroupRenderModel[];
}

const buildUsageTitle = (usage: ServiceUsage): string =>
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  usage.service.title || `${usage.service.code} (Titre non renseigné)`;

const buildEntityName = (
  entity: Readonly<{ title?: string | null; label: string }>,
): string =>
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  entity.title || entity.label;

const buildMaterialRenderModel = (
  materialUsage: MaterialUsageInfo,
): DevisPrestationsMaterialRenderModel => ({
  id: materialUsage._id,
  code: materialUsage.material.code,
  name: buildEntityName(materialUsage.material),
  description: materialUsage.material.content ?? null,
  quantity: materialUsage.quantity,
});

const buildCostMarginRenderModel = (
  costMargin: CostUsageInfo['cost']['costMargin'],
  unitAmount: DevisPrestationsCostAmountRenderModel,
): DevisPrestationsCostMarginRenderModel => {
  if (costMargin.fixedValue) {
    return {
      label: costMargin.label,
      percentage: null,
      amountPerUnit: {
        value: costMargin.fixedValue.value,
        currencyId: costMargin.fixedValue.currencyId,
      },
    };
  }

  const percentage = costMargin.variableValue ?? 0;

  return {
    label: costMargin.label,
    percentage,
    amountPerUnit: {
      value: (unitAmount.value * percentage) / 100,
      currencyId: unitAmount.currencyId,
    },
  };
};

const buildCostRenderModel = (
  costUsage: CostUsageInfo,
  servicePriceExcludingVat: Readonly<{ value: number; currencyId: Id }>,
): DevisPrestationsCostRenderModel => {
  const { cost, quantity } = costUsage;

  const unitAmount: DevisPrestationsCostAmountRenderModel = cost.amount
    ? { value: cost.amount.value, currencyId: cost.amount.currencyId }
    : {
        value: (servicePriceExcludingVat.value * (cost.value ?? 0)) / 100,
        currencyId: servicePriceExcludingVat.currencyId,
      };

  const margin = buildCostMarginRenderModel(cost.costMargin, unitAmount);

  const totalExcludingVatBeforeMargin: DevisPrestationsCostAmountRenderModel = {
    value: unitAmount.value * quantity.value,
    currencyId: unitAmount.currencyId,
  };

  const totalExcludingVatWithMargin: DevisPrestationsCostAmountRenderModel = {
    value: (unitAmount.value + margin.amountPerUnit.value) * quantity.value,
    currencyId: unitAmount.currencyId,
  };

  return {
    id: costUsage._id,
    code: cost.code,
    name: buildEntityName(cost),
    description: cost.content ?? null,
    unitAmount,
    quantity,
    margin,
    totalExcludingVatBeforeMargin,
    totalExcludingVatWithMargin,
  };
};

const sumCostsWithMargin = (
  costs: readonly DevisPrestationsCostRenderModel[],
): DevisPrestationsCostAmountRenderModel => ({
  value: costs.reduce(
    (sum, cost) => sum + cost.totalExcludingVatWithMargin.value,
    0,
  ),
  currencyId: costs[0]?.totalExcludingVatWithMargin.currencyId ?? null,
});

const buildUsageRenderModel = (
  usage: ServiceUsage,
  isManualTotal: boolean,
): DevisPrestationsUsageRenderModel => {
  const { settings, quantity, service } = usage;
  // A null value is one the user may not read: its amounts and costs are not rendered.
  const { value } = service;
  const costs =
    settings.showCostsInQuotation && !isManualTotal && value
      ? service.costUsages.map((costUsage) =>
          buildCostRenderModel(costUsage, value.priceExcludingVat),
        )
      : null;
  const costsSubtotalForOneUnit = costs ? sumCostsWithMargin(costs) : null;
  const costsTotal = costsSubtotalForOneUnit
    ? {
        value: costsSubtotalForOneUnit.value * quantity.value,
        currencyId: costsSubtotalForOneUnit.currencyId,
      }
    : null;

  return {
    id: usage._id,
    title: buildUsageTitle(usage),
    content: service.content ?? null,
    quantity: settings.showQuantitiesInQuotation ? quantity : null,
    amounts:
      settings.showAmountsInQuotation && !isManualTotal && value
        ? {
            priceExcludingVat: value.priceExcludingVat,
            discountPercentage: value.discountPercentage ?? null,
            discountAmount: value.discountAmount ?? null,
            vatId: value.vatId,
          }
        : null,
    materials: settings.showMaterialsInQuotation
      ? service.materialUsages.map(buildMaterialRenderModel)
      : null,
    costs,
    costsSubtotalForOneUnit,
    costsTotal,
  };
};

const buildGroupRenderModel = (
  group: ServiceUsagesGroup,
  groupingMode: GroupingModeEnum,
  previousGroup: ServiceUsagesGroup | undefined,
): DevisPrestationsGroupRenderModel => {
  const isManualTotal = group.totalMode === TotalModeEnum.Manual;

  return {
    id: group._id,
    sectionTitle:
      group.sectionTitle !== previousGroup?.sectionTitle
        ? group.sectionTitle
        : null,
    label: formatQuotationGroupLabel(group),
    subtotal:
      groupingMode === GroupingModeEnum.Manual ? group.effectiveTotal : null,
    usages: group.serviceUsages.map((usage) =>
      buildUsageRenderModel(usage, isManualTotal),
    ),
  };
};

export const buildDevisPrestationsRenderModel = (
  serviceUsagesGroups: readonly ServiceUsagesGroup[],
): DevisPrestationsRenderModel => {
  const groupingMode =
    serviceUsagesGroups[0]?.groupingMode ?? GroupingModeEnum.Singleton;

  return {
    groupingMode,
    groups: serviceUsagesGroups.map((group, position) =>
      buildGroupRenderModel(
        group,
        groupingMode,
        serviceUsagesGroups[position - 1],
      ),
    ),
  };
};
